<script lang="ts">
    import { createSubject, debounceTime } from "light-observable/observable";
    import { Canvas } from "svelte-pixel-editor-wasm";
    import { wasm } from "$lib/wasm/wasm";
    import { onMount } from "svelte";
    import { createInputHandler } from "$lib/image/inputHandler";

    import type { File } from "../wasm/file";
    import { COMMANDS } from "$lib/commands";
    import { frameIndex, interaction, symmetryGuideEditing } from "$lib/state";
    import { EditorSettings } from "$lib/commands/settings";
    import Icon from "$lib/utility/Icon.svelte";

    if (!wasm) throw new Error("WASM not loaded");

    export let state: { pan: Vec2, scale: number };

    export let file: File;

    let htmlCanvas: HTMLCanvasElement;
    const canvas = Canvas.new(file.width, file.height);

    let offscreen: OffscreenCanvas;
    let ctx: OffscreenCanvasRenderingContext2D;
    onMount(() => {
        offscreen = htmlCanvas.transferControlToOffscreen();
        ctx = offscreen.getContext("2d")!;

        resize();
        const [stream, sink] = createSubject<void>();
        const resizeSub = debounceTime(100, stream).subscribe(resize);

        const observer = new ResizeObserver(() => sink.next());
        observer.observe(htmlCanvas.parentElement!);
        return () => {
            observer.disconnect();
            resizeSub.unsubscribe();
        };
    });

    let u8_buffer: Uint8ClampedArray;

    function updateBufferIfDetached() {
        if (u8_buffer === undefined || u8_buffer.buffer !== wasm.memory.buffer) {
            console.info("memory detached, updating...");

            const parent = htmlCanvas.parentElement! as HTMLElement;
            let { width, height } = parent.getBoundingClientRect();
            width = Math.ceil(width * devicePixelRatio);
            height = Math.ceil(height * devicePixelRatio);

            u8_buffer = new Uint8ClampedArray(wasm.memory.buffer, canvas.data_ptr(), width * height * 4);
        }
    }

    function resize() {
        const parent = htmlCanvas.parentElement! as HTMLElement;
        let { width, height } = parent.getBoundingClientRect();
        width = Math.ceil(width * devicePixelRatio);
        height = Math.ceil(height * devicePixelRatio);

        canvas.resize(width, height);
        canvas.overlay_width = Math.ceil(2 * devicePixelRatio);

        u8_buffer = new Uint8ClampedArray(wasm.memory.buffer, canvas.data_ptr(), width * height * 4);

        offscreen.width = width;
        offscreen.height = height;

        draw();
    }

    $: draw(), state, file, gridZoom, tilingPreview;

    let lastDrawTime = 0;

    // Subscribe to redraw
    file.onDataChanged.onMount(([frame, _]) => {
        if (frame !== -1 && frame !== (interaction.value?.frameIndex.value ?? -1)) return;
        draw();
    });
    interaction.value.onCanvasDataChanged.onMount(() => {
        draw();
    });
    onMount(() => frameIndex.subscribe(_ => {
        draw();
    }));
    onMount(() => symmetryGuideEditing.subscribe(_ => draw()));

    $: gridZoom = $EditorSettings["grid-visible"].value ? 2 ** $EditorSettings["grid-visible-at-zoom"].value : 2 ** 16
    $: tilingPreview = $EditorSettings["view-tiling"].value;

    async function draw() {
        if (!htmlCanvas || !offscreen) return;
        if (file.internalIsFree) {
            return; // todo fix; frameIndex event is fired while the file is null
        }

        canvas.grid_zoom_visible = gridZoom;
        (canvas as any).tiling_preview = tilingPreview;
        $interaction?.setWrapEdits(tilingPreview);
        (canvas as any).show_symmetry_guide = $symmetryGuideEditing;
        canvas.set_camera(Math.floor(state.pan.x * devicePixelRatio), Math.floor(state.pan.y * devicePixelRatio), Math.ceil(state.scale));

        const start = performance.now();
        canvas.render($interaction.internal);
        lastDrawTime = performance.now() - start;

        updateBufferIfDetached();
        const result = new ImageData(
            u8_buffer,
            offscreen.width,
            offscreen.height,
        );
        ctx.putImageData(result, 0, 0);
    }

    async function benchmark() {
        const iterations = 500;

        const start = performance.now();
        // canvas.benchmark_draw_file($interaction.internal, iterations);
        const total = performance.now() - start;
        console.log({
            total,
            iterations,
            time: total / iterations,
        });
    }

    onMount(() => {
        const { add, destroy } = COMMANDS.registerActions("all");

        add("general.undo", (steps = 1) => {
            for (let i = 0; i < steps; i++) {
                interaction.value.undo(); // todo redraw
            }
        });
        add("general.redo", (steps = 1) => {
            for (let i = 0; i < steps; i++) {
                interaction.value.redo(); // todo redraw
            }
        });
        return destroy;
    });

    function editorSettingChanged() {
        EditorSettings.save();
        draw();
    }
</script>

<!----------------------------------------------------------------------------->

<canvas class="canvas" bind:this={htmlCanvas}
        style="width: {(offscreen?.width ?? 0) / devicePixelRatio}px; height: {(offscreen?.height ?? 0) / devicePixelRatio}px;"
        use:createInputHandler={{int: $interaction, canvas}}
>
    Your browser does not support the canvas element.
</canvas>

<div class="overlay">
    <label title="Show pixel grid"><Icon id="grid-16" /><input type="checkbox" class="grid-visible" bind:checked={$EditorSettings["grid-visible"].value} on:change={editorSettingChanged} /></label>
    <button
        title="Tiling preview"
        class:active={$EditorSettings["view-tiling"].value}
        on:click={() => COMMANDS.invoke("edit.toggle-view-tiling")}
    ><Icon id="grid-16" /></button>
</div>

<!----------------------------------------------------------------------------->
<style>
    .canvas {
        all: unset;
        position: absolute;
        top: 0;
        left: 0;
        user-select: none;
        image-rendering: pixelated;
    }

    .overlay {
        all: unset;
        position: absolute;
        top: 0;
        right: 0;
        user-select: none;
        padding-inline: 0.5ch;
        display: flex;
        align-items: center;
        gap: 0.25em;
    }

    label, button {
        all: unset;
        color: var(--t400);
        min-width: 1.5em;
        height: 1.5em;
        display: grid;
        place-content: center;
        font-family: var(--font-monospace);
        background: var(--panel-bg);
        border: 1px solid var(--g50);

        & input {
            display: none;
        }

        &:has(input:checked), &.active {
            color: var(--tacc);
            border-color: var(--gacc100);
        }
    }

    button {
        cursor: pointer;
    }
</style>
